import { Box, Typography, CircularProgress, Backdrop } from "@mui/material";
import BasicMenu from "./BasicMenu";
import { AttachMoney, CalendarMonth, PersonOutline, Place } from "@mui/icons-material";
import { Desdobramento } from "../types/negocio/Desdobramento";

interface CardDesdobramentoProps {
    desdobramento: Desdobramento,
    onEdit: () => void,
    onDelete: () => void,
    onViewMore?: () => void,
    isLoading?: boolean,
}

export default function CardDesdobramento({ 
    desdobramento, 
    onEdit, 
    onDelete, 
    onViewMore,
    isLoading = false 
}: CardDesdobramentoProps) {
    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1, 
            p: 2, 
            border: '2px solid #042E4B', 
            borderRadius: 2, 
            backgroundColor: 'white',
            position: 'relative'
        }}>
            {/* Loading overlay */}
            {isLoading && (
                <Backdrop
                    open={true}
                    sx={{
                        position: 'absolute',
                        zIndex: 10,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0
                    }}
                >
                    <CircularProgress color="primary" size={30} />
                </Backdrop>
            )}

            <Box sx={{ display: 'flex', alignItems: 'top', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '16px' }}>
                    {desdobramento.oque}
                </Typography>
                <BasicMenu
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onViewMore={onViewMore}
                    fontSize="small"
                    disabled={isLoading}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Date Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        backgroundColor: (theme) => theme.palette.secondary.main,
                        py: 1,
                        px: 1.25,
                        borderRadius: 2
                    }}
                >
                    <CalendarMonth sx={{ color: 'white' }} fontSize="small" />
                    <Typography sx={{ fontSize: '14px' }} color="white">
                        { desdobramento.quando }
                    </Typography>
                </Box>

                {/* Responsible Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        py: 1,
                        px: 1.25,
                        borderRadius: 2
                    }}
                >
                    <PersonOutline sx={{ color: 'white' }} fontSize="small" />
                    <Typography sx={{ fontSize: '14px' }} color="white">
                        { desdobramento.quemNomeCompleto }
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Money Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#4caf50',
                        py: 1,
                        px: 1.25,
                        borderRadius: 2
                    }}
                >
                    <AttachMoney sx={{ color: 'white' }} fontSize="small" />
                    <Typography sx={{ fontSize: '14px' }} color="white">
                        { desdobramento.quantoCusta ? `R$ ${desdobramento.quantoCusta}` : 'Gratuito' }
                    </Typography>
                </Box>

                {/* Location Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        py: 1,
                        pr: 1.25,
                        pl: 0.75,
                        borderRadius: 2
                    }}
                >
                    <Place sx={{ color: 'white', margin: 0, padding: 0 }} fontSize="small" />
                    <Typography sx={{ fontSize: '14px' }} color="white">
                        { desdobramento.onde || 'Local não especificado' }
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}